import { FC } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import styled from 'styled-components';
import tw from 'twin.macro';

import { defaultOptions, defaultColors } from './defaultOptions';
import strings from '../../language/Swedish';
import { AdsMarketSharesType } from '../../types/apiTypes';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

ChartJS.defaults.font.family = 'Chivo, sans-serif';

const StyledPieChart = styled.div`
  ${tw`flex-1 min-h-60`};
`;

interface Props {
  data: AdsMarketSharesType[];
  chartLabel: string;
  colors?: string[];
  options?: ChartOptions<'pie'>;
  height?: string;
}

const PieChart: FC<Props> = ({ data, chartLabel, colors, options, height }) => {
  const sortedData = data.sort((a, b) => b.market_share - a.market_share);
  const labels = sortedData.slice(0, 10).map(({ firm }) => firm);
  const dataPoints = sortedData
    .slice(0, 10)
    .map(({ market_share }) => market_share);
  const sumOfOthersData =
    sortedData.length > 10
      ? data.slice(10).reduce((acc, item) => acc + item.market_share, 0)
      : 0;

  const chartData: ChartData<'pie', (number | null)[], string> = {
    labels: [
      ...labels,
      ...(sumOfOthersData !== 0 ? [strings.market.othersLabel] : []),
    ],
    datasets: [
      {
        label: chartLabel,
        data: [
          ...dataPoints,
          ...(sumOfOthersData !== 0 ? [sumOfOthersData] : []),
        ],
        borderWidth: 1,
        backgroundColor: colors
          ? [
              ...colors.map((color) => `rgba(${color})`),
              'rgba(148, 163, 184, 0.45)',
            ]
          : [
              ...defaultColors.map((color) => `rgba(${color})`),
              'rgba(148, 163, 184, 0.45)',
            ],
      },
    ],
  };

  return (
    <StyledPieChart>
      <Pie
        data={chartData}
        options={{ ...defaultOptions, ...options }}
        height={height ? height : '400'}
      />
    </StyledPieChart>
  );
};

export default PieChart;
